import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { sqTimezones } from '@/utilities/datetime.constants';
import { Select } from '@seeqdev/qomponents';
import type { Timezone } from '@/datetime/timezone.service';
import type { BivariantCallback } from '@/utilities.types';

interface TimeZoneSelectorProps {
  /** Selected time zone */
  timezone: Timezone;
  /** Alignment for dropdown */
  direction?: 'top' | 'bottom' | 'auto';
  /** Specify True to make selection of a time zone optional */
  optional?: boolean;
  /** Function to call when time zone is selected */
  onSelect: BivariantCallback<Timezone | undefined>;
  /** Specify True to present time zone selector as disabled */
  disabled?: boolean;
  /** CSS class names to apply to parent element around <select> */
  xstyle?: stylex.StyleXStyles;
  extraClassNames?: string;
  /** CSS class name to apply to <select> element */
  selectClassName?: string;
  /** Test ID */
  testId?: string;
}

/**
 * Searchable dropdown selector for selecting a time zone
 */
export const TimeZoneSelectorUnwrapped = (props: TimeZoneSelectorProps) => {
  const {
    timezone,
    direction = 'auto',
    optional = false,
    onSelect,
    disabled = false,
    extraClassNames = '',
    selectClassName,
    testId,
  } = props;

  const groupedZones = _.chain(sqTimezones.timezones)
    .map((zone) => ({ value: zone, label: zone.displayName }))
    .sortBy('label')
    .groupBy((option) => option.value.offsetMinutes)
    .map((options) => ({ label: options[0].value.offset, options }))
    .sortBy((group) => group.options[0].value.offsetMinutes)
    .value();

  return (
    <div
      id="timeZoneSelector"
      data-testid={testId || 'timeZoneSelector'}
      {...stylex.props(props.xstyle)}
      className={clsx(stylex.props(props.xstyle).className, extraClassNames)}>
      <Select
        value={{ label: timezone?.displayName, value: timezone }}
        options={groupedZones}
        extraClassNames={selectClassName ?? ''}
        isClearable={optional}
        onChange={(newZone: { value: Timezone } | null) => onSelect(newZone?.value)}
        menuPortalTarget={document.body}
        menuPlacement={direction}
        isDisabled={disabled}
      />
    </div>
  );
};

export const TimeZoneSelector = React.memo(
  TimeZoneSelectorUnwrapped,
  (prev, next) =>
    !(
      !_.isEqual(prev.timezone, next.timezone) ||
      prev.disabled !== next.disabled ||
      prev.xstyle !== next.xstyle ||
      prev.extraClassNames !== next.extraClassNames
    ),
);

export default TimeZoneSelector;
